import * as React from "react"
import { ElementType, HTMLProps, useEffect, useState } from "react"
import styled, { DefaultTheme } from "styled-components"
import { CloseStrokeIcon } from "../../icons/CloseStroke"
import { ValidIcon } from "../../icons/Valid"
import { theme } from "../../provider"
import { colors } from "../colors"

type ToggleVariant = "primary" | "secondary"

const getToggleBackground = (theme: DefaultTheme, checked: boolean, $variant?: ToggleVariant) => {
  if (!checked) return theme.colors.grey.$300

  switch ($variant) {
    case "primary":
      return colors.majorelle
    case "secondary":
      return colors.indigo
    default:
      return theme.colors.grey.$600
  }
}

const ToggleCmp = styled.button.attrs<{ checked: boolean; $variant?: ToggleVariant }>((props) => ({
  role: "switch",
  type: "button",
  "aria-checked": props.checked,
  ...props,
}))<{ checked: boolean; $animationOff: boolean; $variant?: ToggleVariant }>`
  width: 2.75rem;
  height: 1.5rem;

  background-color: ${({ theme, checked, $variant }) => getToggleBackground(theme, checked, $variant)};
  border: none;
  border-radius: 3.75rem;
  transition: all 0.4s ease;
  cursor: pointer;
  padding: 0 0.25rem;

  &::after {
    content: "";
    display: block;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;

    transition: all 0.4s ease;

    background-color: ${({ theme }) => theme.colors.white};
  }

  &:hover {
    background-color: ${({ theme, checked }) => (checked ? theme.colors.black.$1000 : theme.colors.grey.$400)};
  }

  &:focus {
    outline: 4px solid ${({ theme }) => theme.colors.grey.$400};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.grey.$300};
  }

  &[aria-checked="false"]::after {
    animation: linear toggleOff forwards;
    animation-duration: ${({ $animationOff }) => ($animationOff ? "0" : "0.3s")};
  }

  &[aria-checked="true"]::after {
    animation: linear toggleOn forwards;
    animation-duration: ${({ $animationOff }) => ($animationOff ? "0" : "0.3s")};
  }

  @keyframes toggleOn {
    from {
      transform: translateX(0);
      width: 1rem;
    }

    50% {
      width: 1.5rem;
    }

    to {
      width: 1rem;
      transform: translateX(1.25rem);
    }
  }
  @keyframes toggleOff {
    from {
      transform: translateX(1.25rem);
      width: 1rem;
    }

    50% {
      width: 1.5rem;
    }

    to {
      width: 1rem;
      transform: translateX(0);
    }
  }
`

const ValidInside = styled(ValidIcon)`
  position: absolute;
  animation: linear fadein 0.5s;
  z-index: 1;
  width: 16px;
  height: 16px;
  right: 0;

  @keyframes fadein {
    from {
      opacity: 0;
    }

    @50% {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`
const CloseStrokeInside = styled(CloseStrokeIcon)`
  position: absolute;
  animation: linear fadein 0.8s;
  z-index: 1;
  width: 16px;
  height: 16px;
  left: 0;

  @keyframes fadein {
    from {
      opacity: 0;
    }

    @50% {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`

const IconContainer = styled.div`
  position: relative;
`

export type ToggleProps = {
  checked: boolean
  $variant?: ToggleVariant
  as?: ElementType
} & HTMLProps<HTMLButtonElement> &
  ({ "aria-label": string } | { "aria-labelledby": string })

export const DeprecatedToggle = React.forwardRef((props: ToggleProps, ref) => {
  // Disable uneeded animation on first render
  const [animationOff, setAnimationOff] = useState(true)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnimationOff(false)
    }, 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [])
  return (
    <ToggleCmp {...props} ref={ref} $animationOff={animationOff}>
      {!props.disabled ? (
        <IconContainer>
          {props.checked ? (
            <ValidInside color={theme.colors.grey.$600} />
          ) : (
            <CloseStrokeInside color={theme.colors.grey.$400} />
          )}
        </IconContainer>
      ) : null}
    </ToggleCmp>
  )
})
